import type {
  Provider,
  Role,
  Permission,
  RolePermissions,
  Tier,
  TierLimits,
  ProviderCategory,
  ServiceCategory,
  AnalyzerConfig,
  AntiPatternId,
  AntiPatternDefinition,
  LetterGrade,
  CompanySize,
  Department,
  Industry,
  AiTool,
  CoachingProfile,
} from './types';

export const SITE_URL = 'https://www.getverbal.ai';

export const ROLE_PERMISSIONS: RolePermissions = {
  owner: [
    'manage_org',
    'manage_users',
    'manage_connectors',
    'view_usage',
    'manage_usage',
    'view_prompt_content',
    'manage_alerts',
    'manage_budgets',
    'export_data',
    'view_audit_logs',
    'run_reconcile',
  ],
  admin: [
    'manage_org',
    'manage_users',
    'manage_connectors',
    'view_usage',
    'manage_usage',
    'view_prompt_content',
    'manage_alerts',
    'manage_budgets',
    'export_data',
    'view_audit_logs',
    'run_reconcile',
  ],
  finance: [
    'view_usage',
    'manage_budgets',
    'export_data',
    'view_audit_logs',
  ],
  team_lead: [
    'view_usage',
    'manage_usage',
    'view_prompt_content',
    'manage_alerts',
    'export_data',
  ],
  member: [
    'view_usage',
    'manage_usage',
  ],
  readonly: [
    'view_usage',
  ],
} as const;

// --- Gated signup options ---

export const COMPANY_SIZES: CompanySize[] = [
  '1-10',
  '11-50',
  '51-200',
  '201-1000',
  '1000+',
];

export const DEPARTMENTS: { value: Department; label: string }[] = [
  { value: 'engineering', label: 'Engineering' },
  { value: 'product', label: 'Product' },
  { value: 'finance', label: 'Finance' },
  { value: 'operations', label: 'Operations' },
  { value: 'executive', label: 'Executive' },
  { value: 'other', label: 'Other' },
];

export const INDUSTRIES: { value: Industry; label: string }[] = [
  { value: 'technology', label: 'Technology' },
  { value: 'finance', label: 'Finance / Banking' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'retail', label: 'Retail / E-commerce' },
  { value: 'other', label: 'Other' },
];

export const AI_TOOLS: { value: AiTool; label: string }[] = [
  { value: 'openai', label: 'OpenAI / ChatGPT' },
  { value: 'anthropic', label: 'Anthropic / Claude' },
  { value: 'google', label: 'Google Gemini' },
  { value: 'github_copilot', label: 'GitHub Copilot' },
  { value: 'midjourney', label: 'Midjourney' },
  { value: 'cursor', label: 'Cursor' },
  { value: 'perplexity', label: 'Perplexity' },
  { value: 'other', label: 'Other' },
];

/** End date of the launch promotional period. After this date, normal tier gating resumes. */
export const PROMO_END_DATE = new Date('2026-10-01T00:00:00Z');

/**
 * Whether the promotional period is active as of `now` (defaults to the
 * current instant).
 *
 * Deliberately a function, NOT a module-load constant. A server worker that
 * imports this module before PROMO_END_DATE and stays warm past the cutoff
 * (serverless workers can live for hours) must still see the promo end at
 * the configured instant, not at next cold start. Always call this fresh —
 * never cache the result in a module-scope variable.
 */
export function isPromoActive(now: Date = new Date()): boolean {
  return now.getTime() < PROMO_END_DATE.getTime();
}

/**
 * "Month YYYY" label derived from PROMO_END_DATE for user-facing copy
 * (banners, tooltips). Keeps promo text in sync with the constant so
 * extending the promo never requires a second edit to hardcoded copy.
 */
export const PROMO_END_DATE_LABEL = PROMO_END_DATE.toLocaleDateString('en-US', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

/** Tier completeness ordering — used only to floor the promo elevation below. */
const TIER_ORDER: Tier[] = ['free', 'personal_pro', 'team', 'business'];

/**
 * Resolves the effective tier for feature/limit gating while the promo is
 * active. The promo only ever ELEVATES a tier — a free org is treated as
 * personal_pro — it must never DOWNGRADE a paying team/business org.
 *
 * Without this floor, the naive `isPromoActive() ? 'personal_pro' : tier`
 * pattern strips team/business-exclusive features (and, worse, resource
 * limits like maxUsers — team's cap is 25, personal_pro's is 1) from real
 * paying customers for the entire promo window. Always route promo-aware
 * tier elevation through this helper instead of that ternary.
 *
 * `now` defaults to the current time and is computed fresh on every call
 * (via `isPromoActive`) — never cached — so a long-lived server worker
 * resumes normal gating at PROMO_END_DATE without needing a restart.
 */
export function resolvePromoTier(tier: Tier, now: Date = new Date()): Tier {
  if (!isPromoActive(now)) return tier;
  return TIER_ORDER.indexOf(tier) >= TIER_ORDER.indexOf('personal_pro') ? tier : 'personal_pro';
}

export const SYSTEM_MIN_RETENTION_DAYS = 30;

export const DISCREPANCY_THRESHOLD_PERCENT = 5;

export const DISCREPANCY_THRESHOLD_DOLLARS = 100;

export const SUPPORTED_PROVIDERS: Provider[] = ['openai', 'anthropic', 'google'];

export function roleHasPermission(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

// =============================================================================
// Phase 6: Differentiation constants
// =============================================================================

// --- Tier system ---

export const TIER_LIMITS: Record<Tier, TierLimits> = {
  free: {
    maxUsers: 1,
    maxTrackedSubscriptions: 5,
    maxApiKeys: 2,
    retentionDays: 14,
    features: {
      browserExtension: true,
      roiTracking: false,
      exports: false,
      scheduledReports: false,
      teamDashboard: false,
      sharedBudgets: false,
      departmentRollups: false,
      usagePolicies: false,
      auditLogs: false,
      legalHolds: false,
      sso: false,
      apiAccess: false,
      encryption: false,
      prioritySupport: false,
      promptCoaching: false,
      sessionReviews: false,
      advancedFilters: false,
      csvExport: false,
      canRegenerateBrief: false,
      toolAnalytics: false,
      promptOptimization: true,
      priceAlerts: false,
      goals: false,
      benchmarkPercentiles: false,
      projects: false,
      parallelismInsights: false,
      roiAutoEstimate: false,
      harnessAudit: false,
      rulesTuner: false,
      recommendationsSetup: true,
    },
  },
  personal_pro: {
    maxUsers: 1,
    maxTrackedSubscriptions: Infinity,
    maxApiKeys: 5,
    retentionDays: 365,
    features: {
      browserExtension: true,
      roiTracking: true,
      exports: true,
      scheduledReports: true,
      teamDashboard: false,
      sharedBudgets: false,
      departmentRollups: false,
      usagePolicies: false,
      auditLogs: false,
      legalHolds: false,
      sso: false,
      apiAccess: false,
      encryption: false,
      prioritySupport: false,
      promptCoaching: true,
      sessionReviews: true,
      advancedFilters: true,
      csvExport: true,
      canRegenerateBrief: true,
      toolAnalytics: true,
      // Re-enabled 2026-07-30 (founder decision): all individual features unlocked.
      // Previously dark-launched pending PR #744 unit economics validation (judge rewriter).
      promptOptimization: true,
      priceAlerts: true,
      goals: true,
      benchmarkPercentiles: true,
      projects: true,
      parallelismInsights: true,
      roiAutoEstimate: true,
      harnessAudit: true,
      rulesTuner: true,
      recommendationsSetup: true,
    },
  },
  team: {
    maxUsers: 25,
    maxTrackedSubscriptions: Infinity,
    maxApiKeys: 25,
    retentionDays: 365,
    features: {
      browserExtension: true,
      roiTracking: true,
      exports: true,
      scheduledReports: true,
      teamDashboard: true,
      sharedBudgets: true,
      departmentRollups: true,
      usagePolicies: true,
      auditLogs: false,
      legalHolds: false,
      sso: false,
      apiAccess: false,
      encryption: true,
      prioritySupport: false,
      promptCoaching: true,
      sessionReviews: true,
      advancedFilters: true,
      csvExport: true,
      canRegenerateBrief: true,
      toolAnalytics: true,
      promptOptimization: true,
      priceAlerts: true,
      goals: true,
      benchmarkPercentiles: true,
      projects: true,
      parallelismInsights: true,
      roiAutoEstimate: true,
      harnessAudit: true,
      rulesTuner: true,
      recommendationsSetup: true,
    },
  },
  business: {
    maxUsers: Infinity,
    maxTrackedSubscriptions: Infinity,
    maxApiKeys: Infinity,
    retentionDays: Infinity,
    features: {
      browserExtension: true,
      roiTracking: true,
      exports: true,
      scheduledReports: true,
      teamDashboard: true,
      sharedBudgets: true,
      departmentRollups: true,
      usagePolicies: true,
      auditLogs: true,
      legalHolds: true,
      sso: true,
      apiAccess: true,
      encryption: true,
      prioritySupport: true,
      promptCoaching: true,
      sessionReviews: true,
      advancedFilters: true,
      csvExport: true,
      canRegenerateBrief: true,
      toolAnalytics: true,
      promptOptimization: true,
      priceAlerts: true,
      goals: true,
      benchmarkPercentiles: true,
      projects: true,
      parallelismInsights: true,
      roiAutoEstimate: true,
      harnessAudit: true,
      rulesTuner: true,
      recommendationsSetup: true,
    },
  },
} as const;

// --- Feedback widget surfaces (§10.4 magic strings) ---
export const FEEDBACK_SURFACES = [
  'recs_page',
  'onboarding_checklist',
  'weekly_digest',
] as const;
export type FeedbackSurface = (typeof FEEDBACK_SURFACES)[number];

export const FREE_TIER_MAX_SUBSCRIPTIONS = 5;
export const FREE_TIER_MAX_API_KEYS = 2;

// --- Provider & service categories ---

export const PROVIDER_CATEGORIES: ProviderCategory[] = [
  'llm_consumer',
  'image_gen',
  'code_assistant',
  'ai_saas',
  'infrastructure',
  'other',
];

export const SERVICE_CATEGORIES: ServiceCategory[] = [
  'llm_hosting',
  'vector_db',
  'gpu_compute',
  'ml_platform',
  'embedding',
  'fine_tuning',
  'storage',
  'other',
];

// --- Task types ---

export const TASK_TYPES = [
  'code_generation',
  'writing',
  'analysis',
  'image_creation',
  'research',
  'translation',
  'summarization',
  'email_drafting',
  'data_extraction',
  'other',
] as const;

export type TaskType = (typeof TASK_TYPES)[number];

// --- Prompt analysis ---

export const PROMPT_SCORE_DIMENSIONS = [
  'clarity',
  'specificity',
  'structure',
  'efficiency',
  'context',
] as const;

export type PromptScoreDimension = (typeof PROMPT_SCORE_DIMENSIONS)[number];

export const PROMPT_ISSUE_TYPES = [
  'vague_instruction',
  'missing_context',
  'no_examples',
  'excessive_preamble',
  'redundant_content',
  'poor_formatting',
  'missing_constraints',
  'wrong_model_for_task',
  'no_output_format',
  'ambiguous_intent',
] as const;

export type PromptIssueType = (typeof PROMPT_ISSUE_TYPES)[number];

export const PROMPT_CATEGORIES = [
  'code_generation',
  'writing',
  'analysis',
  'creative',
  'conversation',
  'data_extraction',
  'research',
  'translation',
  'summarization',
] as const;

export type PromptCategory = (typeof PROMPT_CATEGORIES)[number];

/**
 * Maps usage_events.source values to coaching profiles. Keyed as Record<string, …>
 * because sources extend beyond the EventSource union (e.g. mcp_hook, trace, provider_api).
 */
export const SOURCE_TO_COACHING_PROFILE: Record<string, CoachingProfile> = {
  mcp: 'dev_tool',
  mcp_hook: 'dev_tool',
  sdk_hook: 'dev_tool',
  otel: 'dev_tool',
  trace: 'dev_tool',
  sdk: 'api',
  provider_api: 'api',
  claude_import: 'conversational',
  chatgpt_import: 'conversational',
  browser_extension: 'conversational',
  manual: 'conversational',
  import: 'conversational',
};

export const PROMPT_BADGES: Record<string, { name: string; description: string; criteria: string }> = {
  first_90_score: {
    name: 'Sharp Prompter',
    description: 'First prompt scoring 90+',
    criteria: 'Score 90 or above on any single prompt',
  },
  week_streak: {
    name: 'Streak Master',
    description: '7 consecutive days of improvement',
    criteria: 'Average score improves each day for 7 days straight',
  },
  '100_prompts': {
    name: 'Centurion',
    description: '100 prompts analyzed',
    criteria: 'Have 100 total prompts analyzed',
  },
  efficiency_master: {
    name: 'Efficiency Master',
    description: 'Avg efficiency score above 80 for a week',
    criteria: 'Maintain 80+ avg efficiency score for 7 consecutive days',
  },
  zero_waste_day: {
    name: 'Zero Waste',
    description: 'All prompts scored 80+ on efficiency',
    criteria: 'Every prompt in a single day scores 80+ on efficiency',
  },
  most_improved: {
    name: 'Most Improved',
    description: 'Biggest week-over-week score jump',
    criteria: 'Largest single-week improvement in overall score',
  },
};

export const ANALYZER_DEFAULTS: AnalyzerConfig = {
  model: 'claude-3-haiku',
  maxPromptsPerDay: 50,
  minTokenThreshold: 20,
};

/**
 * Minimum session tokens to show the "no score" indicator on session cards.
 * Defined in the central gate registry (`lib/data-gates`).
 */
export { MIN_TOKENS_FOR_SCORE_INDICATOR } from '@/lib/data-gates';

// --- Tier pricing (reference, not billing logic) ---

export const TIER_PRICING = {
  free: { monthly: 0, annual: 0 },
  personal_pro: { monthly: 29, annual: 278 },
  team: { monthlyPerUser: 19, annualPerUser: 190 },
  business: { monthlyPerUser: 25, annualPerUser: 240 },
} as const;

// --- Analysis cost caps per tier ---

export const ANALYSIS_COST_CAPS: Record<Tier, number> = {
  free: 0,
  personal_pro: 5,
  team: 5, // per user
  business: 10, // per user
};

// --- Letter grade thresholds ---

export const LETTER_GRADE_THRESHOLDS: Record<LetterGrade, number> = {
  A: 90,
  B: 70,
  C: 40,
  D: 20,
  F: 0,
};

// --- Anti-pattern definitions ---

// ---------------------------------------------------------------------------
// Pattern → Tool & Technique Recommendations
// ---------------------------------------------------------------------------

export interface PatternRecommendation {
  tool: string;
  technique: string;
}

/** Maps anti-pattern IDs to actionable tool and prompting technique recommendations. */
export const PATTERN_RECOMMENDATIONS: Partial<Record<string, PatternRecommendation>> = {
  vague_prompting: {
    tool: 'Create a Claude Project with a system prompt defining expected output format',
    technique: 'Lead with action verbs: "Write", "Refactor", "Analyze", "List"',
  },
  missing_context: {
    tool: 'Create a CLAUDE.md in your project root — Claude loads it automatically',
    technique: 'Open with: "You are a [role]. Context: [project details]."',
  },
  excessive_preamble: {
    tool: "Save repeated context to a Claude Project so you don't re-explain each session",
    technique: 'Skip pleasantries — start directly with the task',
  },
  no_examples: {
    tool: 'Add 2–3 examples to your Claude Project system prompt (reuse across sessions)',
    technique: 'Add inline examples: "Like this: [A], [B]"',
  },
  poor_formatting: {
    tool: 'Add a Markdown template to your Claude Project system prompt',
    technique: 'Use numbered steps and ## Section headers',
  },
};

export const ANTI_PATTERN_DEFINITIONS: Record<AntiPatternId, AntiPatternDefinition> = {
  vague_prompting: {
    name: 'Vague Prompting',
    description: 'Prompts lack specific instructions, leading to irrelevant or off-target responses and wasted tokens on retries.',
    severity: 'high',
    relatedIssueTypes: ['vague_instruction', 'ambiguous_intent'],
    lesson: {
      title: 'Write Specific Prompts',
      body: 'Replace vague requests like "write something about X" with concrete instructions: specify the audience, format, length, and key points to cover.',
    },
  },
  missing_context: {
    name: 'Missing Context',
    description: 'Prompts omit necessary background information, forcing the model to guess or hallucinate details.',
    severity: 'high',
    relatedIssueTypes: ['missing_context', 'missing_constraints'],
    lesson: {
      title: 'Provide Relevant Context',
      body: 'Include the who, what, and why. Specify constraints (language, tone, length) and provide reference material when available.',
    },
  },
  no_examples: {
    name: 'No Examples Provided',
    description: 'Complex tasks lack examples of the desired output, increasing token waste from misformatted responses.',
    severity: 'medium',
    relatedIssueTypes: ['no_examples'],
    lesson: {
      title: 'Use Few-Shot Examples',
      body: 'For structured or formatted output, include 1-3 examples of the desired result. This dramatically reduces retries and improves first-pass quality.',
    },
  },
  excessive_preamble: {
    name: 'Excessive Preamble',
    description: 'Prompts include unnecessary filler text that burns input tokens without improving output quality.',
    severity: 'medium',
    relatedIssueTypes: ['excessive_preamble', 'redundant_content'],
    lesson: {
      title: 'Trim the Filler',
      body: 'Remove pleasantries, unnecessary background, and repeated instructions. Every token should serve a purpose. Front-load the important context.',
    },
  },
  poor_formatting: {
    name: 'Poor Formatting',
    description: 'Unstructured prompts make it harder for the model to parse intent, leading to lower quality responses.',
    severity: 'low',
    relatedIssueTypes: ['poor_formatting', 'no_output_format'],
    lesson: {
      title: 'Structure Your Prompts',
      body: 'Use headers, bullet points, and numbered lists to organize complex prompts. Specify the desired output format explicitly (JSON, markdown, etc.).',
    },
  },
  wrong_model: {
    name: 'Wrong Model for Task',
    description: 'Using an expensive model for simple tasks or a cheap model for complex ones wastes money or produces poor results.',
    severity: 'medium',
    relatedIssueTypes: ['wrong_model_for_task'],
    lesson: {
      title: 'Match Model to Task',
      body: 'Use smaller, cheaper models for simple classification, extraction, and formatting. Reserve large models for complex reasoning, creative writing, and nuanced analysis.',
    },
  },
};

// ---------------------------------------------------------------------------
// Session event fetching
// ---------------------------------------------------------------------------

/**
 * Hard ceiling on events fetched for a single session.
 *
 * Above the largest session observed in production (15,103 events), so in
 * practice this only exists to stop a pathological session exhausting memory.
 * When it is hit, callers are told so the UI can say the transcript is partial
 * rather than presenting an under-count as the session's true total.
 *
 * Lives here rather than in dashboard-queries/sessions.ts because
 * SessionDetailPanel is a client component: importing it from the query module
 * drags supabase/server.ts (and `node:fs`) into the browser bundle and fails
 * the build.
 */
export const MAX_SESSION_EVENTS = 20_000;
